import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Code2, ExternalLink } from 'lucide-react';
import { absoluteUrl } from '../utils/seo';

interface Props {
  forumId: string;
  forumName: string;
  darkMode: boolean;
  className?: string;
}

const EMBED_HEIGHT = 300;

const ForumEmbedCard: React.FC<Props> = ({ forumId, forumName, darkMode, className = '' }) => {
  const embedUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set('theme', darkMode ? 'dark' : 'light');
    params.set('accent', '#6366f1');
    params.set('compact', '1');
    return `${absoluteUrl(`/embed/${encodeURIComponent(forumId)}`)}?${params.toString()}`;
  }, [forumId, darkMode]);

  const docsUrl = `/embed-docs?forum_id=${encodeURIComponent(forumId)}`;

  const box = darkMode
    ? 'bg-gray-800/80 border-gray-700 text-gray-100'
    : 'bg-indigo-50/90 border-indigo-100 text-gray-900';

  return (
    <section
      className={`rounded-xl border p-4 sm:p-6 ${box} ${className}`}
      aria-labelledby="forum-embed-heading"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2
            id="forum-embed-heading"
            className={`text-lg font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-indigo-900'}`}
          >
            <Code2 className="text-indigo-500 shrink-0" size={22} />
            Embed görünümü
          </h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {forumName} istatistiklerini kendi sitenizde gösterin
          </p>
        </div>
      </div>

      <div
        className={`rounded-xl overflow-hidden border ${
          darkMode ? 'border-gray-600 bg-gray-900/40' : 'border-indigo-100 bg-white'
        }`}
      >
        <iframe
          src={embedUrl}
          width="100%"
          height={EMBED_HEIGHT}
          scrolling="no"
          title={`${forumName} widget önizlemesi`}
          className="block w-full"
          style={{ border: 0 }}
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          to={docsUrl}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
            darkMode
              ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          <Code2 size={16} />
          Sitene ekle
        </Link>
        <a
          href={embedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
            darkMode
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
              : 'border-indigo-200 text-indigo-700 hover:bg-indigo-50'
          }`}
        >
          <ExternalLink size={16} />
          Widget’ı aç
        </a>
      </div>
    </section>
  );
};

export default ForumEmbedCard;
